import React from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Divider,
  Breadcrumbs,
  Link,
  Paper,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button
} from '@mui/material';
import EmojiObjectsIcon from '@mui/icons-material/EmojiObjects';
import SchoolIcon from '@mui/icons-material/School';
import TimelineIcon from '@mui/icons-material/Timeline';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import { Link as RouterLink } from 'react-router-dom';
import SeoHead from '../components/common/SeoHead';
import LazyImage from '../components/common/LazyImage';

// 导入图片
import bgImage from '../assets/images/collections/Bg.png';
import luopanImage from '../assets/images/collections/luopan.png';
import L1Image from '../assets/images/Luopan/L1.png';
import L2Image from '../assets/images/Luopan/L2.png';
import L3Image from '../assets/images/Luopan/L3.png';
import L4Image from '../assets/images/Luopan/L4.png';
import L5Image from '../assets/images/Luopan/L5.png';

const FortuneGuide = () => {
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // 结构化数据
  const fortuneGuideSchemaData = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "命理知识：了解八字、紫微斗数、塔罗牌的基础知识",
    "description": "探索中国传统命理学的基础知识，包括八字命盘、紫微斗数和现代塔罗牌占卜等命理体系。了解如何通过这些古老智慧获得人生指引。",
    "image": "https://destiny-crystal.com/assets/images/collections/luopan.png",
    "author": {
      "@type": "Organization",
      "name": "DestinyCrystal命理水晶"
    },
    "publisher": {
      "@type": "Organization",
      "name": "DestinyCrystal命理水晶",
      "logo": {
        "@type": "ImageObject",
        "url": "https://destiny-crystal.com/assets/images/logo.png"
      }
    },
    "datePublished": "2025-03-26",
    "dateModified": "2025-03-26"
  };

  // 内容部分
  const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`fortune-tabpanel-${index}`}
        aria-labelledby={`fortune-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box sx={{ p: 3 }}>
            {children}
          </Box>
        )}
      </div>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 6 }}>
      {/* SEO优化 */}
      <SeoHead
        title="命理知识：八字、紫微斗数、塔罗牌解析 | DestinyCrystal"
        description="探索中国传统命理学的基础知识，包括八字命盘、紫微斗数和现代塔罗牌占卜等命理体系。了解如何通过这些古老智慧获得人生指引。"
        canonicalUrl="https://destiny-crystal.com/fortune-guide"
        imageUrl="https://destiny-crystal.com/assets/images/collections/luopan.png"
        keywords={["命理知识", "八字命盘", "紫微斗数", "塔罗牌", "命理学基础", "生辰八字", "命理解析"]}
        schemaData={fortuneGuideSchemaData}
      />

      {/* 面包屑导航 */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" color="inherit">
          首页
        </Link>
        <Typography color="text.primary">命理知识</Typography>
      </Breadcrumbs>

      <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ mb: 1 }}>
        命理知识
      </Typography>
      <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 5 }}>
        探索传统命理学的奥秘与智慧
      </Typography>

      {/* 封面图 */}
      <Box sx={{ position: 'relative', mb: 6, borderRadius: 4, overflow: 'hidden', height: 300 }}>
        <LazyImage 
          src={bgImage} 
          alt="命理知识背景" 
          sx={{ height: '100%', filter: 'brightness(0.7)' }}
        />
        <Box sx={{ 
          position: 'absolute', 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)',
          textAlign: 'center',
          width: '100%',
          px: 2,
          color: 'white'
        }}>
          <Typography variant="h4" gutterBottom>
            解读生命密码，探寻人生方向
          </Typography>
          <Typography variant="body1" sx={{ maxWidth: 700, mx: 'auto' }}>
            命理学是透过生辰信息解读人生的古老智慧，它不是迷信，而是一套系统的人生规划工具
          </Typography>
        </Box>
      </Box>

      {/* 标签导航 */}
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 'medium',
              py: 2
            }
          }}
        >
          <Tab icon={<SchoolIcon />} label="命理学基础" />
          <Tab icon={<TimelineIcon />} label="八字命盘" />
          <Tab icon={<PersonIcon />} label="紫微斗数" />
          <Tab icon={<StarIcon />} label="塔罗牌解读" />
        </Tabs>
      </Paper>

      {/* 内容面板 */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>
              什么是命理学？
            </Typography>
            <Typography variant="body1" paragraph>
              命理学是中国传统文化的重要组成部分，源于古代对天文历法、阴阳五行的观察与研究。它不同于西方的占星术，更加注重人与自然、宇宙的关系，以及个体命运与大环境的互动。
            </Typography>
            <Typography variant="body1" paragraph>
              命理学认为，每个人出生的年、月、日、时形成独特的"命盘"，记录了此人的先天禀赋、性格特点、机遇挑战等信息。通过解读这些信息，可以帮助人们了解自己的优势、劣势，以及生命中可能遇到的机遇与挑战。
            </Typography>
            <Typography variant="body1" paragraph>
              虽然现代科学尚未能完全解释命理学的原理，但这套系统已存在数千年，并被无数人用作自我认知与人生规划的工具。在当代社会，命理学可以被视为一种自我了解和个人成长的辅助手段，而非命定论的决定性因素。
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ position: 'relative', height: '100%', minHeight: 300 }}>
              <Box sx={{ 
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '80%',
                maxWidth: 300,
                height: 'auto'
              }}>
                <LazyImage 
                  src={luopanImage} 
                  alt="罗盘" 
                />
              </Box>
              <Box sx={{ 
                position: 'absolute',
                top: '15%',
                left: '20%',
                width: '15%',
                height: 'auto',
                animation: 'float 8s ease-in-out infinite'
              }}>
                <LazyImage src={L1Image} alt="命理元素" />
              </Box>
              <Box sx={{ 
                position: 'absolute',
                top: '20%',
                right: '20%',
                width: '15%',
                height: 'auto',
                animation: 'float 7s ease-in-out infinite 1s'
              }}>
                <LazyImage src={L2Image} alt="命理元素" />
              </Box>
              <Box sx={{ 
                position: 'absolute',
                bottom: '25%',
                left: '25%',
                width: '15%',
                height: 'auto',
                animation: 'float 9s ease-in-out infinite 2s'
              }}>
                <LazyImage src={L3Image} alt="命理元素" />
              </Box>
              <Box sx={{ 
                position: 'absolute',
                bottom: '15%',
                right: '25%',
                width: '15%',
                height: 'auto',
                animation: 'float 10s ease-in-out infinite 3s'
              }}>
                <LazyImage src={L4Image} alt="命理元素" />
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 3 }} />
            <Typography variant="h5" gutterBottom>
              命理学的主要体系
            </Typography>
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      八字命理
                    </Typography>
                    <Typography variant="body2" paragraph>
                      以生辰八字为基础，分析天干地支的相互作用，解读个人的先天命盘。
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <EmojiObjectsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="源自汉代，由徐升《三命通会》系统化" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <EmojiObjectsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="基于阴阳五行、干支历法理论" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <EmojiObjectsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="适合分析性格、职业适应性等" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      紫微斗数
                    </Typography>
                    <Typography variant="body2" paragraph>
                      综合星宿与时空关系，构建详细的人生宫位图，预测人生各方面的发展。
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <EmojiObjectsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="源自宋代，由陈希夷创立" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <EmojiObjectsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="结合星象学与中国传统哲学" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <EmojiObjectsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="适合详细分析人生不同阶段的变化" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      塔罗牌占卜
                    </Typography>
                    <Typography variant="body2" paragraph>
                      结合直觉与牌面象征意义，解读当下情境与未来可能性，提供指引。
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemIcon>
                          <EmojiObjectsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="起源于15世纪欧洲，融合多种神秘学" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <EmojiObjectsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="78张牌代表不同的原型与能量" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <EmojiObjectsIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="适合解答具体问题和近期变化" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={7}>
            <Typography variant="h5" gutterBottom>
              八字命盘的基础知识
            </Typography>
            <Typography variant="body1" paragraph>
              八字命盘，又称"四柱命理"，是以一个人出生的年、月、日、时为基础，每柱包含一个天干和一个地支，共八个字，因此称为"八字"。这八个字构成了命盘的基本结构，揭示了一个人的先天命运和性格特质。
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              天干地支基础
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>天干</strong>：甲、乙、丙、丁、戊、己、庚、辛、壬、癸（共10个）<br />
              <strong>地支</strong>：子、丑、寅、卯、辰、巳、午、未、申、酉、戌、亥（共12个）
            </Typography>
            <Typography variant="body1" paragraph>
              天干地支与五行相对应：<br />
              甲乙属木，丙丁属火，戊己属土，庚辛属金，壬癸属水。<br />
              同样，12地支也有对应的五行属性和藏干关系。
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              八字的四柱结构
            </Typography>
            <Typography variant="body1" paragraph>
              八字命盘由四柱构成：年柱、月柱、日柱、时柱。<br />
              - <strong>年柱</strong>：代表祖先、长辈影响，以及人生大环境<br />
              - <strong>月柱</strong>：代表父母、成长环境和中年运势<br />
              - <strong>日柱</strong>：代表自己、配偶和核心性格<br />
              - <strong>时柱</strong>：代表子女、晚年运势和内在潜能
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              日主与喜用神
            </Typography>
            <Typography variant="body1" paragraph>
              日柱天干被称为"日主"，代表命主本人的核心能量。日主五行的强弱决定了命局的基本特点。根据日主五行的状态，确定"喜用神"和"忌神"，这是八字调侯的核心。
            </Typography>
            <Typography variant="body1" paragraph>
              例如，如果一个人日主五行属水且偏弱，那么水和金可能是喜用神（金生水），而土可能是忌神（土克水）。了解自己的喜用神，可以在人生选择中有所侧重，比如在事业选择、居住环境等方面向有利的方向发展。
            </Typography>
          </Grid>
          
          <Grid item xs={12} md={5}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom align="center">
                八字命盘示例
              </Typography>
              <Box sx={{ width: '100%', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>四柱</th>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>年柱</th>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>月柱</th>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>日柱</th>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>时柱</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>天干</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>甲</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>丙</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', backgroundColor: '#e3f2fd' }}>戊</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>辛</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>地支</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>寅</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>午</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', backgroundColor: '#e3f2fd' }}>子</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>未</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>五行</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>木/木</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>火/火</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', backgroundColor: '#e3f2fd' }}>土/水</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>金/土</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>藏干</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>甲丙戊</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>丙己庚</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center', backgroundColor: '#e3f2fd' }}>癸</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>己丁乙</td>
                    </tr>
                  </tbody>
                </table>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                <strong>注</strong>：上表为示例命盘，突出显示的日柱为日主，代表命主本人。
              </Typography>
            </Paper>
            
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                八字的应用领域
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="性格解读" 
                    secondary="分析先天性格特点、优势与弱点，帮助自我认知" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="事业方向" 
                    secondary="根据五行喜忌分析适合的职业领域与工作环境" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="人际关系" 
                    secondary="分析与他人的五行相合相克，预测互动模式" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="大运流年" 
                    secondary="分析人生不同阶段的运势变化，提前做好规划" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="风水选择" 
                    secondary="根据命局特点选择适合的居住环境与方位" 
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>
              紫微斗数简介
            </Typography>
            <Typography variant="body1" paragraph>
              紫微斗数是中国古代最复杂精密的命理学体系之一，源于宋代。它以北斗七星为核心，结合天文历法与阴阳五行，建立了一套详尽的人生宫位系统。紫微斗数命盘由十二宫位组成，分别对应人生的不同领域，通过星曜的落宫与交互，解读人生的各个方面。
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              紫微斗数的核心特点
            </Typography>
            <Typography variant="body1" paragraph>
              与八字命理主要关注五行生克不同，紫微斗数更注重星曜的组合与宫位关系。它的独特之处在于能够细致地分析人生不同领域的情况，如事业、财富、婚姻、健康、子女等，并且能够预测这些领域在不同时期的发展变化。
            </Typography>
            <Typography variant="body1" paragraph>
              紫微斗数的排盘需要准确的出生年、月、日、时，通过特定的计算方法确定命宫位置，然后按规则安排其他宫位和星曜。整个命盘就像一幅详细的人生地图，揭示了命主在各个领域的潜能和挑战。
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              主要星曜
            </Typography>
            <Typography variant="body1" paragraph>
              紫微斗数命盘中有数十颗星曜，其中最重要的是"十四主星"：
            </Typography>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="紫微星" secondary="君臣之首，象征领导能力" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="天机星" secondary="智慧与创造力的象征" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="太阳星" secondary="代表名誉与光明" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="武曲星" secondary="财富与实践能力的象征" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="天同星" secondary="仁慈与和谐的象征" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="廉贞星" secondary="正直与变化的象征" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="天府星" secondary="福德与积累的象征" />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={6}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="太阴星" secondary="情感与直觉的象征" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="贪狼星" secondary="欲望与进取心的象征" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="巨门星" secondary="口才与应变能力的象征" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="天相星" secondary="美德与和平的象征" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="天梁星" secondary="仁爱与社交能力的象征" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="七杀星" secondary="权威与决断力的象征" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="破军星" secondary="开拓与冲击力的象征" />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom align="center">
                紫微斗数的十二宫位
              </Typography>
              <Typography variant="body2" paragraph>
                紫微斗数命盘分为十二宫位，每个宫位对应人生的一个重要领域：
              </Typography>
              <Box sx={{ width: '100%', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>宫位</th>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>象征意义</th>
                      <th style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>关键解读</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>命宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>个人特质、外貌</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>人生基调、性格特点、个人形象</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>兄弟宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>手足关系、同事</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>与兄弟姐妹、同事、同辈的关系</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>夫妻宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>婚姻、伴侣关系</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>婚姻质量、伴侣特质、情感发展</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>子女宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>后代、创作</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>子女数量、特质、亲子关系</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>财帛宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>财富、收入</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>财富积累能力、收入来源</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>疾厄宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>健康、疾病</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>体质、易患疾病、养生之道</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>迁移宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>旅行、变动</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>出行、搬迁、环境变化</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>奴仆宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>下属、助手</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>与下属、助手的关系，交通工具</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>官禄宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>事业、地位</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>职业发展、社会地位、成就</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>田宅宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>房产、家庭</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>居住条件、不动产、家庭环境</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>福德宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>内心、福气</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>内在价值观、心灵满足、幸福感</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '10px', border: '1px solid #ddd', textAlign: 'center' }}>父母宫</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>长辈、权威</td>
                      <td style={{ padding: '10px', border: '1px solid #ddd' }}>与父母长辈关系，处理权威态度</td>
                    </tr>
                  </tbody>
                </table>
              </Box>
            </Paper>
            
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                紫微斗数的现代应用
              </Typography>
              <Typography variant="body2" paragraph>
                虽然起源古老，紫微斗数在现代仍有广泛应用：
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="职业规划" 
                    secondary="通过分析官禄宫、财帛宫等，确定适合的职业方向" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="关系分析" 
                    secondary="分析夫妻宫、父母宫等，了解人际关系模式" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="财富管理" 
                    secondary="根据财帛宫、田宅宫分析，制定合适的理财策略" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="大运分析" 
                    secondary="了解人生不同阶段的运势变化，做好规划" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="健康指导" 
                    secondary="通过疾厄宫分析，了解体质特点和保健重点" 
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Typography variant="h5" gutterBottom>
              塔罗牌解读入门
            </Typography>
            <Typography variant="body1" paragraph>
              塔罗牌是一种源于15世纪欧洲的神秘学工具，由78张牌组成，分为大阿卡那(22张)和小阿卡那(56张)。每张牌都有其独特的象征意义和能量，通过抽取并解读这些牌，可以获得对于问题的洞察和未来的预示。
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              大阿卡那：生命旅程的象征
            </Typography>
            <Typography variant="body1" paragraph>
              大阿卡那有22张牌，从愚者(0)到世界(21)，象征着一个人从懵懂无知到智慧完满的精神旅程。这些牌代表生命中的重大主题和转折点，能够揭示深层的心理状态和潜意识信息。
            </Typography>
            <Typography variant="body1" paragraph>
              例如，"愚者"代表新的开始和无限可能，"高塔"象征突然的变化和打破旧有结构，"太阳"则代表成功、喜悦和丰盛。每张牌都有其正位和逆位解读，提供了丰富的分析视角。
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              小阿卡那：日常生活的反映
            </Typography>
            <Typography variant="body1" paragraph>
              小阿卡那包含56张牌，分为四个花色：权杖(火)、圣杯(水)、宝剑(风)、钱币(土)。每个花色有Ace到10的数字牌和四张宫廷牌(侍从、骑士、皇后、国王)。
            </Typography>
            <Typography variant="body1" paragraph>
              权杖代表创造力、行动和热情；圣杯关注情感、关系和直觉；宝剑涉及思想、沟通和挑战；钱币则与物质世界、财富和实际事务相关。小阿卡那通常用来解读日常生活的具体情况和短期发展。
            </Typography>
            
            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              塔罗牌阵法
            </Typography>
            <Typography variant="body1" paragraph>
              塔罗牌阵是指抽取牌的特定排列方式，不同的牌阵适合不同类型的问题和解读深度。常见的牌阵包括：
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <EmojiObjectsIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="单牌解读" 
                  secondary="抽取一张牌，获取简单明确的答案或每日指引" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EmojiObjectsIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="三张牌阵" 
                  secondary="过去-现在-未来，或问题-挑战-建议等简单解读" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EmojiObjectsIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="凯尔特十字牌阵" 
                  secondary="10张牌的经典牌阵，提供全面深入的问题分析" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EmojiObjectsIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="关系牌阵" 
                  secondary="专门用于分析两人关系的特定牌阵" 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <EmojiObjectsIcon fontSize="small" color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="生命树牌阵" 
                  secondary="基于卡巴拉生命树，提供深度精神与灵性分析" 
                />
              </ListItem>
            </List>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h6" gutterBottom align="center">
                大阿卡那牌的核心寓意
              </Typography>
              <Box sx={{ maxHeight: 400, overflow: 'auto', pr: 1 }}>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="0 愚者" 
                      secondary="新的开始、自发性、纯真、冒险、潜能" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="I 魔术师" 
                      secondary="创造力、技能、意志力、自信、主动性" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="II 女祭司" 
                      secondary="直觉、内在智慧、神秘、知识、潜意识" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="III 女皇" 
                      secondary="丰饶、滋养、母性、创造、自然、感受性" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="IV 皇帝" 
                      secondary="权威、结构、秩序、父性、领导力" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="V 教皇" 
                      secondary="精神指导、传统、信仰、教育、团体认同" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="VI 恋人" 
                      secondary="爱、关系、选择、价值观、和谐、吸引力" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="VII 战车" 
                      secondary="决心、胜利、控制、意志力、自信、成功" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="VIII 力量" 
                      secondary="内在力量、勇气、说服力、毅力、温和" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="IX 隐士" 
                      secondary="内省、寻找、独处、指导、智慧、谨慎" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="X 命运之轮" 
                      secondary="变化、循环、命运、转折点、机会" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XI 正义" 
                      secondary="公正、平衡、真相、因果、决策、清晰" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XII 倒吊人" 
                      secondary="牺牲、暂停、放下、新视角、转变" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XIII 死神" 
                      secondary="结束、转变、蜕变、新开始、必然变化" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XIV 节制" 
                      secondary="平衡、调和、融合、目的、流动、节制" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XV 恶魔" 
                      secondary="束缚、依赖、物质主义、欲望、阴影面" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XVI 高塔" 
                      secondary="突变、崩解、启示、解放、不稳定" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XVII 星星" 
                      secondary="希望、信心、灵感、宁静、更新" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XVIII 月亮" 
                      secondary="幻象、恐惧、潜意识、不确定性、直觉" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XIX 太阳" 
                      secondary="成功、喜悦、明朗、正面能量、自信" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XX 审判" 
                      secondary="觉醒、更新、评估、重生、转变、呼唤" 
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <StarIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="XXI 世界" 
                      secondary="完成、整合、成就、旅行、和谐统一" 
                    />
                  </ListItem>
                </List>
              </Box>
            </Paper>
            
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                塔罗牌解读技巧
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="准备工作" 
                    secondary="在静心冥想后提出清晰问题，创造安静平和的环境" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="信任直觉" 
                    secondary="塔罗解读结合规则与直觉，信任第一反应和象征联想" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="注意牌之间关系" 
                    secondary="注意牌与牌之间的互动、共性或对比，形成整体叙事" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="牌面象征细节" 
                    secondary="关注牌面的颜色、数字、人物表情等细节，丰富解读" 
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <EmojiObjectsIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="保持开放心态" 
                    secondary="避免只寻找想要的答案，保持中立客观的态度" 
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </TabPanel>

      {/* 底部号召性按钮 */}
      <Box sx={{ textAlign: 'center', mt: 8, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          寻找专业命理指引？
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 600, mx: 'auto' }}>
          我们的专业命理师可以为您提供更深入、更个性化的命理解读。无论是八字分析、紫微斗数解盘还是塔罗牌指引，都能为您的人生提供清晰方向。
        </Typography>
        <Grid container spacing={2} justifyContent="center">
          <Grid item>
            <Button
              component={RouterLink}
              to="/fortune-teller"
              variant="contained"
              color="primary"
              size="large"
            >
              预约命理师
            </Button>
          </Grid>
          <Grid item>
            <Button
              component={RouterLink}
              to="/ai-fortune"
              variant="outlined"
              color="primary"
              size="large"
            >
              体验AI算命
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default FortuneGuide;